// Hand-typed against mood/tina/docs/orchestrator-api-handoff.md §5.
// Pipeline-side row called "Item" in IMS to avoid clashing with the music
// `events` resource. Wire keys keep their orchestrator names.

export type RunStatus = "queued" | "running" | "done" | "failed" | "cancelled";

export type ItemStatus =
  | "pending"
  | "in_progress"
  | "waiting_on_primary"
  | "failed_retryable"
  | "failed"
  | "done"
  | "skipped"
  | "duplicate"
  | "cancelled"
  | "hitl";

/**
 * Mirror of `src/db/enums.py::FinalOutcome`.
 *
 * A `const` tuple, not a bare union: `FINAL_OUTCOMES` is what the `/tina/items`
 * outcome filter enumerates, so a value added here reaches the filter without a
 * second edit, and the label map in `@/lib/tina/constants` is a
 * `Record<FinalOutcome, string>` — adding a member without labelling it is a
 * compile error rather than a silently stale dropdown.
 */
export const FINAL_OUTCOMES = [
  "created",
  "draft",
  "manual_review",
  "duplicate",
  "skipped",
  "failed",
  "cancelled",
  "discovered",
  // No `auto_created`: Tina removed it (never written; 2026-09-24), and it
  // validates `GET /events?final_outcome=` against its enum, so a filter
  // offering it would 422.
  // An enrichment job that wrote the entity back as `Enriched`.
  "enriched",
  // The five an event-refresh job can settle on (`refresh_apply` owns them all,
  // except `refresh_unchanged`, which `refresh_diff`'s short-circuit stamps).
  // A refresh job that parks stamps `manual_review` like every other HITL, so
  // there is no `refresh_hitl`; and there is no `refresh_skipped` either —
  // `src/db/enums.py::FinalOutcome` has no such member.
  "refresh_unchanged",
  "refresh_updated",
  "refresh_dismissed",
  "refresh_shadow",
  "refresh_retired",
] as const;

export type FinalOutcome = (typeof FINAL_OUTCOMES)[number];

/**
 * Mirror of `src/db/enums.py::HitlReason`. Same `const` tuple contract as
 * {@link FINAL_OUTCOMES}: `HITL_REASON_OPTIONS` and `HITL_REASON_TO_ENTITY_TYPE`
 * are both derived from it, so the union and the filter cannot drift apart.
 */
export const HITL_REASONS = [
  "venue_no_address",
  "venue_places_miss",
  "venue_low_confidence",
  "venue_geodata_miss",
  "lineup_too_ambiguous",
  "artist_ambiguous_match",
  "artist_spotify_miss",
  "artist_spotify_no_image",
  "artist_low_confidence",
  "movie_tmdb_miss",
  "movie_low_confidence",
  "movie_ambiguous_match",
  "play_ambiguous_match",
  "play_no_image_found",
  "person_ambiguous_match",
  "low_score",
  "image_low_confidence",
  // An enrichment proposal the confidence gate parked.
  "enrichment_low_confidence",
  // An automatic enrichment write disagreed with a curated field —
  // artist-field-locks (2026-09-28). Parks on a dedicated
  // `enrichment_artist_curation` job, never on `enrichment_artist`.
  "enrichment_curator_review",
  // A refresh job found the source page disagreeing with the published event,
  // or found the page gone. Migration `0059`.
  "refresh_review",
] as const;

export type HitlReason = (typeof HITL_REASONS)[number];

export type StageExecutionStatus =
  | "ok"
  | "inherited"
  | "failed_retryable"
  | "failed"
  | "skipped"
  | "hitl"
  | "duplicate";

export interface Page<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * The three run classes, and `GET /runs?job_kind=`' values.
 *
 * **None of them is a `trigger_type`.** `runs.trigger_type` is only ever
 * `manual`/`cron`, so an enrichment run is typed `manual` (no enrichment
 * caller is a scrape cron) and a refresh run is typed `cron` (the sweep rides the
 * cron tick). Both are told apart only by `trigger_spec` — see {@link runJobKind}.
 */
export const RUN_JOB_KINDS = ["scrape", "enrichment", "refresh"] as const;
export type RunJobKind = (typeof RUN_JOB_KINDS)[number];

export type ManualTrigger = {
  type: "manual";
  sites?: string[];
  urls?: string[];
  limit?: number;
  /**
   * An external enrichment run rides on `type: "manual"` and carries its own
   * attribution as extra keys (Tina's `ManualTrigger` is `extra="allow"`). All
   * four are absent on every scrape run, and `job_kind` is absent on every run
   * written before the marker existed — which is why "scrape" is the *absence*
   * of the marker, never `job_kind === "scrape"`.
   */
  job_kind?: RunJobKind;
  /** An `EnrichmentSourceKind` — `backend-create`, `ims`, `bypass`, … */
  source?: string;
  entity_count?: number;
  /** Provenance of the asker: a uid, `cron-backstop`, `tina-walk:<uuid>`, … */
  requested_by?: string;
  /**
   * A dependent repair's parent — the entity whose own enrichment changed and
   * forced these jobs. Present on `source: "repair"` runs and only those (699
   * of them in prod on 2026-09-02, all four keys set together, against zero on
   * every other source). `requested_by` restates it as
   * `repair:<component_type>:<component_id>`, which is why the raw string is
   * never what the UI renders.
   */
  reason?: string;
  component_type?: string;
  component_id?: string;
  /** The `events` row that enriched the parent — the job that caused this run. */
  parent_event_id?: string;
};
export type CronTrigger = { type: "cron"; sites?: string[] };
/**
 * One hour of the nightly event-refresh sweep — mirror of tina's `RefreshTrigger`
 * (`src/db/schemas/trigger_spec.py`).
 *
 * **`runs.trigger_type` is `"cron"` on a refresh run**, not `"refresh"`: the column only
 * ever holds manual/cron and the sweep runs off the cron tick. So the class is told
 * apart by `trigger_spec.type` / `trigger_spec.job_kind`, never by `trigger_type` — see
 * {@link runJobKind}.
 *
 * `hour` is tina's `refresh_run_hour_key`: the run's UTC hour floor, `"2026-09-03T01:00Z"`. It is
 * the run's identity — the sweep does a find-or-create on it, so every job seeded in that hour
 * (including a daytime duplicate-hook job) lands in the same run.
 */
export type RefreshTrigger = {
  type: "refresh";
  job_kind: "refresh";
  hour: string;
  sites?: string[];
};
export type TriggerSpec = ManualTrigger | CronTrigger | RefreshTrigger;

export interface RunRead {
  id: string;
  status: RunStatus;
  trigger_type: string;
  trigger_spec: TriggerSpec;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  stats: Record<string, unknown> | null;
  triggered_by: string | null;
}

export interface RunSummaryRow {
  run_id: string;
  status: RunStatus;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  event_count: number;
  created_count: number;
  hitl_count: number;
  duplicate_count: number;
  skipped_count: number;
  failed_count: number;
  total_cost: string | null;
  root_count: number;
  child_count: number;
  discover_seed_count: number;
  discover_seed_failed_count: number;
}

export interface RunDetail {
  run: RunRead;
  summary: RunSummaryRow;
}

export interface LlmModelCostRow {
  model: string | null;
  calls: number;
  input_tokens: number;
  output_tokens: number;
  thinking_tokens: number;
  cached_tokens: number;
  cost: number;
}

export interface ApiSkuCostRow {
  sku: string;
  calls: number;
  cost: number;
}

export interface ApiProviderCostRow {
  api: string;
  calls: number;
  cost: number;
  skus: ApiSkuCostRow[];
}

export interface RunCostBreakdown {
  run_id: string;
  total_cost: number;
  llm: {
    total_cost: number;
    models: LlmModelCostRow[];
  };
  api: {
    total_cost: number;
    providers: ApiProviderCostRow[];
  };
}

export interface InheritanceFlags {
  venue: "shared" | "diverged";
  lineup: "shared" | "diverged";
  type: "shared" | "diverged";
}

export interface VenueNoAddressProblem {
  reason: "venue_no_address";
  extracted_venue_name: string | null;
  extracted_venue_name_original: string | null;
  extracted_venue_address?: string | null;
  country: string | null;
  normalized_search_query?: string | null;
  candidate_count?: number | null;
  rejection_reason?: string | null;
  resolver_stats?: Record<string, unknown>;
}

export interface VenuePlacesMissProblem {
  reason: "venue_places_miss";
  extracted_venue_name: string | null;
  extracted_venue_name_original: string | null;
  extracted_venue_address: string | null;
  country: string | null;
  normalized_search_query?: string | null;
  candidate_count?: number | null;
  rejection_reason?: string | null;
  resolver_stats?: Record<string, unknown>;
}

interface VenueCandidateReviewProblem {
  extracted_venue_name?: string | null;
  extracted_venue_name_original?: string | null;
  extracted_venue_address?: string | null;
  country?: string | null;
  normalized_search_query?: string | null;
  candidate_count?: number | null;
  places_candidates: Array<Record<string, unknown>>;
  gemini_chose_index: number | null;
  gemini_confidence: number | null;
  gemini_reasoning: string | null;
  selected_candidate?: Record<string, unknown> | null;
  rejection_reason?: string | null;
  geodata_candidate?: Record<string, unknown> | null;
  selected_candidate_full?: Record<string, unknown> | null;
  maps_url?: string | null;
  resolver_stats?: Record<string, unknown>;
}

export interface VenueLowConfidenceProblem extends VenueCandidateReviewProblem {
  reason: "venue_low_confidence";
}

export interface VenueGeodataMissProblem extends VenueCandidateReviewProblem {
  reason: "venue_geodata_miss";
}

export interface LineupTooAmbiguousProblem {
  reason: "lineup_too_ambiguous";
  per_artist: Array<Record<string, unknown>>;
  ambiguous_count: number;
  ambiguous_ratio: number;
  thresholds_used: Record<string, number>;
  gemini_reasoning: string;
}

export interface ArtistResolutionProblem {
  reason:
    | "artist_ambiguous_match"
    | "artist_low_confidence"
    | "artist_spotify_miss"
    | "artist_spotify_no_image";
  resolved_artists: Array<Record<string, unknown>>;
  per_artist_audit: Array<Record<string, unknown>>;
  unmatched_ratio: number;
  thresholds_used: Record<string, number>;
  gemini_reasoning: string;
}

export interface MovieTmdbMissProblem {
  reason: "movie_tmdb_miss";
  movie_evidence: Record<string, unknown>;
  search_attempts: Array<Record<string, unknown>>;
  notes: string;
}

export interface MovieLowConfidenceProblem {
  reason: "movie_low_confidence";
  movie_evidence: Record<string, unknown>;
  candidates: Array<Record<string, unknown>>;
  search_attempts: Array<Record<string, unknown>>;
  notes: string;
}

export interface MovieAmbiguousMatchProblem {
  reason: "movie_ambiguous_match";
  movie_evidence: Record<string, unknown>;
  candidates: Array<Record<string, unknown>>;
  search_attempts: Array<Record<string, unknown>>;
  gemini_tie_break: Record<string, unknown>;
  notes: string;
}

export interface PlayAmbiguousMatchProblem {
  reason: "play_ambiguous_match";
  review_reason: string;
  play_evidence: Record<string, unknown>;
  performance_payload: Record<string, unknown>;
  candidates: Array<Record<string, unknown>>;
  search_attempts: Array<Record<string, unknown>>;
  notes: string;
}

export interface PlayNoImageFoundProblem {
  reason: "play_no_image_found";
  play_evidence: Record<string, unknown> | null;
  performance_payload: Record<string, unknown>;
  media: Record<string, unknown> | null;
  selected_image_candidate: Record<string, unknown> | null;
  search_attempts: Array<Record<string, unknown>>;
  notes: string;
}

export interface PersonAmbiguousMatchProblem {
  reason: "person_ambiguous_match";
  target: Record<string, unknown>;
  play_evidence: Record<string, unknown>;
  performance_payload: Record<string, unknown>;
  candidates: Array<Record<string, unknown>>;
  search_attempts: Array<Record<string, unknown>>;
  notes: string;
}

export interface LowScoreProblem {
  reason: "low_score";
  score_breakdown: Record<string, unknown>;
}

export interface ImageLowConfidenceProblem {
  reason: "image_low_confidence";
  chosen_image: { ref?: string; url?: string | null; source?: string };
  verdict_reason: string;
  reference_image_urls: string[];
}

/**
 * Mirror of `src/enrichment/types.py::EnrichmentProposal` — the proposed DTO a
 * parked enrichment carries **inline**.
 *
 * Inline, not a `proposal_ref`: the artifact writer never raises by design, so a
 * required ref made the park payload unconstructible on any GCS blip. There is
 * therefore **no** `GET /hitl/{id}/proposal` route to call —
 * `EventRead.hitl_context` already ships the whole thing.
 */
export interface EnrichmentProposal {
  entity_type: string;
  entity_id: string;
  confidence?: number | null;
  reasoning?: string | null;
  /** The proposed enrichment DTO, camelCase, exactly as the backend takes it. */
  payload: Record<string, unknown>;
  /** `enrichmentSources[]` as it crossed the wire, replayed verbatim on approve. */
  sources?: Record<string, unknown>[];
  /** `greekTranslation` — movie enrichments only. */
  greek_translation?: Record<string, unknown> | null;
  /** `trackPreviews` — artist enrichments only. */
  track_previews?: Record<string, unknown>[] | null;
}

/**
 * Mirror of `src/db/schemas/hitl_context.py::EnrichmentLowConfidenceProblem`.
 *
 * `entity_type` is a plain string on purpose — it is the *enrichment* entity
 * axis (artist | venue | movie | person | show_genre), a different vocabulary
 * from {@link HitlEntityType}, and keeping it loose is what lets a row written
 * by an older deploy still render when the enum grows a tenth flow.
 */
export interface EnrichmentLowConfidenceProblem {
  reason: "enrichment_low_confidence";
  entity_type: string;
  entity_id: string;
  confidence: number | null;
  threshold: number;
  reasoning?: string | null;
  proposal: EnrichmentProposal;
  /** Source *domains* for display ("spotify.com"), not `EnrichmentSourceDto`s. */
  enrichment_sources?: string[];
  /** The scrape event whose tail produced the proposal; absent for external jobs. */
  origin_event_id?: string | null;
  /** Parks so far on this job row, counting this one. Tina caps it at 3. */
  park_count?: number;
}

/**
 * One curated field an automatic write proposed a value for that disagrees with what a
 * curator already put there — mirror of tina's per-entry shape on
 * `EnrichmentCuratorReviewProblem.fields`.
 *
 * `declinedAt` may arrive as `declined_at` instead: Tina's problem is snake_case except
 * this list, "which come through as Tina serializes them" — read either spelling through
 * `curatorReviewDeclinedAt` in `curator-review-resolution.tsx` rather than trusting the key
 * name here.
 */
export interface CuratorReviewFieldHold {
  field: string;
  current: unknown;
  proposed: unknown;
  declinedAt?: string | null;
  declined_at?: string | null;
}

/**
 * Mirror of tina's `EnrichmentCuratorReviewProblem` (artist-field-locks, 2026-09-28).
 *
 * Parked on a dedicated `enrichment_artist_curation` job — never on the ordinary
 * `enrichment_artist` flow `EnrichmentLowConfidenceProblem` covers — whenever
 * `POST /api/artists/link-spotify` or `PUT /api/artists/enrich` held back a curated field
 * because the incoming value disagreed with what a human curator last wrote. `entity_type`
 * is `"artist"` today (per-field curation has not reached any other entity) but read as
 * a plain string, like `EnrichmentLowConfidenceProblem.entity_type`, so a value this build
 * predates or outlives still parses.
 */
export interface EnrichmentCuratorReviewProblem {
  reason: "enrichment_curator_review";
  entity_type: string;
  entity_id: string;
  artist_name?: string | null;
  fields: CuratorReviewFieldHold[];
  /** The scrape event whose tail write produced the hold; absent for an external write. */
  origin_event_id?: string | null;
}

/**
 * One change a refresh job's compare stage proposes — mirror of tina's `ProposedChange`
 * (`src/db/schemas/refresh.py`).
 *
 * `old` is what the published Mood event says (null when the field was blank), `new` what the
 * page now says. Both are strings on the wire whatever the kind: an ISO 8601 timestamp for
 * `date`, JSON for `venue`/`lineup`, the raw text for everything else. `evidence` is a verbatim
 * quote from the page, capped at 300 chars by the prompt — it is the only thing that lets a
 * curator check the model without opening the source.
 */
export interface ProposedChange {
  kind: RefreshChangeKind;
  old: string | null;
  new: string;
  /** 0..1. Tina already dropped anything under `TINA_REFRESH_MIN_CONFIDENCE` (0.6). */
  confidence: number;
  evidence: string;
  /** A `ticket_url` proposal's price, read off the fetched page (MOO-4822). Null on every other
   * kind, and on a ticket proposal whose page states none. */
  price?: number | null;
  /** The ISO 4217 code for `price`. */
  currency?: string | null;
  /** A `date` proposal's end: an ISO 8601 timestamp, set only on a takeover, where the full
   * night's page replaces a small published listing and accepting the date also writes this end
   * (owner ruling on takeovers, 2026-09-29). Absent on every other kind and on every ordinary
   * `date` proposal, which moves the start alone. */
  new_end?: string | null;
}

/**
 * The per-performer breakdown behind a `lineup` proposal — mirror of tina's `LineupDiff`.
 *
 * Present on `hitl_context.problem` **only** when the park carries a `lineup` proposal, and
 * absent entirely from every park written before the per-name path shipped. A `lineup`
 * proposal's `old`/`new` are JSON arrays on the wire; this is the same information already
 * set-differenced, so the panel can offer one tick per name instead of one verdict for the
 * whole roster.
 *
 * **Render it, never re-derive it.** The comparison lives in Python on purpose: it is
 * already filtered against tina's per-name decision memory, so a name a curator declined on
 * an earlier refresh of the same event does not come back as a fresh addition. A TypeScript
 * set-difference of `old` against `new` would not know that, and would drift the moment the
 * backend's normalisation (casing, punctuation, "&" vs "and") changed.
 *
 * `unchanged` is context, not a decision: those performers are on both sides and no
 * `RefreshLineupNameDecision` is ever emitted for them.
 */
export interface RefreshLineupDiff {
  /** On the page, not on the published event — offered as `action: "add"`. */
  additions: string[];
  /** On the published event, no longer on the page — offered as `action: "remove"`. */
  removals: string[];
  /** On both sides. Shown for context; never ticked, never sent. */
  unchanged: string[];
}

export interface RefreshMoveDuplicate {
  /** The OTHER published event this one now collides with. */
  admin_event_id: string;
  name: string;
  venue_name: string;
  start: string | null;
  ims_url: string;
  /** 0-1, the duplicate check's own confidence. */
  confidence: number;
  reasoning: string;
}

/** Which of the two events survives. Mirror of tina's `duplicate_action`. */
export type RefreshDuplicateAction =
  | "keep_this"
  | "keep_other"
  | "not_duplicate";

/**
 * Mirror of `src/db/schemas/hitl_context.py::RefreshReviewProblem`.
 *
 * Two shapes in one payload, and the panel has to branch on `page_gone` rather than on
 * `proposals.length`:
 *
 * - **proposals** — the page disagrees with the published event about something that is already
 *   filled in, so Tina refuses to overwrite it without a curator. Anything it could fill into a
 *   *blank* it already wrote before parking; those kinds are listed in `blanks`, which is
 *   therefore a note and not a decision.
 * - **`page_gone`** — three consecutive bad fetches, three nights unlisted on a `discover_carry`
 *   site, or a compare that said the page is now about a different event. No proposals; the
 *   curator picks a `page_gone_action` instead.
 *
 * `ims_url` is **deliberately not used for the in-app link.** Tina builds it as
 * `…/events/show/{id}` (`stages/apply.py::_ims_url`), a route IMS declares on the resource but
 * never mounts — only `edit/:id`, `create` and `enrichment/:id` exist — so following it 404s.
 * `moodEventHref()` in `@/lib/tina/refresh-jobs` derives the working path from
 * `admin_event_id` instead.
 */
export interface RefreshReviewProblem {
  reason: "refresh_review";
  /** The published Mood event's id — the refresh job's subject, and its HITL dedup key. */
  admin_event_id: string;
  /** Tina's own idea of the IMS deep link. See the note above: prefer `moodEventHref`. */
  ims_url: string;
  source_url: string;
  mode: RefreshMode;
  page_gone: boolean;
  page_gone_reason?: string | null;
  /** Set when a venue or date move landed this event on another published one. */
  duplicate_of?: RefreshMoveDuplicate | null;
  /**
   * How many consecutive bad nights fed this park, when tina counted one — the fetch
   * stage's strike counter. Optional and nullable: a park raised by the compare verdict
   * (`focal_missing`, `same_event_false`) rather than a strike streak carries none, and so
   * does any `page_gone` park written before this field shipped.
   */
  page_gone_strikes?: number | null;
  proposals: ProposedChange[];
  /** Published fields that were empty at compare time — Tina fills these itself. */
  blanks: RefreshChangeKind[];
  /** `gs://` ref of the fetched page's clean markdown, for the artifact viewer. */
  evidence_markdown_ref?: string | null;
  /** `gs://` refs of new poster candidates; render through `GcsImage`, never hotlinked. */
  image_refs: string[];
  /**
   * The published event's CURRENT poster, as a Mood CDN URL (owner ruling R36).
   *
   * A plain URL rather than a `gs://` ref, and rendered with a plain `<img>`:
   * this one is already published, served from our own CDN, and needs no
   * artifact proxy — unlike `image_refs`, whose candidates come from scraped
   * hosts that send `Cross-Origin-Resource-Policy: same-origin`.
   *
   * Optional, and null-able within that: a park written before R36 shipped
   * carries no key at all, and an event with only placeholder art carries
   * `null`. Both mean "nothing to compare against", which is the same render.
   */
  current_poster_url?: string | null;
  /** `mode === "dup"` only: the Tina item whose page was caught as a duplicate. */
  dup_source_event_id?: string | null;
  dup_confidence?: number | null;
  /**
   * Every source URL the published event has been seen on (owner ruling R30) —
   * the same entries as its `metadata.sources`, snapshotted when the job parked.
   *
   * Optional because a park written before R30 shipped carries none, and because
   * the panel treats "no sources recorded" and "the key is absent" the same way:
   * it renders nothing.
   */
  sources?: RefreshContextSource[];
  /**
   * The per-performer diff behind the `lineup` proposal, when there is one.
   *
   * Absent on every park that carries no `lineup` proposal, and on every park written
   * before the per-name path shipped. Both cases mean the same thing to the panel: fall
   * back to the whole-roster Accept/Reject, which is exactly what it did before. See
   * {@link RefreshLineupDiff}.
   */
  lineup_diff?: RefreshLineupDiff | null;
  /**
   * The change kinds tina ALREADY wrote to the event before parking (owner ruling R70) — the
   * blank fills and the enabled automatic changes. `proposals` is strictly what is still open,
   * so this is how the panel can say the event has already moved. Absent on a pre-R70 park.
   */
  auto_applied?: RefreshChangeKind[];
  /**
   * What each of those automatic writes actually changed — before and after (2026-09-11).
   * Mirror of tina's `RefreshAppliedChange`. Absent on a park written before it shipped, in
   * which case the panel falls back to the bare kind chips of `auto_applied`.
   */
  auto_applied_changes?: RefreshAppliedChange[];
}

/** One automatic write, before and after. Mirror of tina's `RefreshAppliedChange`. */
export interface RefreshAppliedChange {
  kind: RefreshChangeKind;
  /** The published value at write time; null when the field was blank. */
  old?: string | null;
  /** The value written — for `poster`, the 1-based candidate position, as on the proposal. */
  new: string;
  confidence: number;
  evidence?: string;
  /**
   * The proposed ticket price and its ISO 4217 code (MOO-4822), carried **only** on a
   * `ticket_url` change whose proposal had one — `null`/absent on every other kind, which
   * leaves their records byte-identical to what they were before the pair existed.
   *
   * The pair is the whole point of the field on this kind: a `ticket_url` change is
   * all-or-nothing on the URL, so a price move (which is auto-applied — it never parks)
   * would otherwise be recorded as `<URL> → <URL>` and read as nothing having happened.
   * Rendered by `refresh-resolution.tsx`'s `TicketPrice`, beside the URL it belongs to.
   */
  price?: number | null;
  /** The ISO 4217 code for `price`. */
  currency?: string | null;
}

/**
 * One `hitl_context.problem.sources` entry — tina's `RefreshSource`, **snake_case**.
 *
 * Deliberately NOT `IEventSource`, which is the same data in the shape Mood stores it.
 * Tina's `RefreshSource.to_patch_dict()` is the only place the camelCase
 * `seenAt`/`updatedFields` keys are produced, and it runs at the Mood PATCH boundary —
 * so an event's `metadata.sources` is camelCase while the HITL context this panel reads
 * is snake_case, verbatim off `RefreshSource.model_dump()`.
 *
 * Typing it as `IEventSource` was silently lossy: `readEventSources` only knew the
 * camelCase spellings, so every refresh review rendered its sources with no "last
 * compared" timestamp and no updated-field chips. That reader now accepts both, and this
 * type says which one actually arrives here.
 */
export interface RefreshContextSource {
  url: string;
  site?: string;
  /** UTC ISO 8601 — the last time tina compared that page, not the first sighting. */
  seen_at?: string;
  /** Mood field names this URL actually changed (`startDate`, `about`, …). */
  updated_fields?: string[];
}

export type HitlProblem =
  | VenueNoAddressProblem
  | VenuePlacesMissProblem
  | VenueLowConfidenceProblem
  | VenueGeodataMissProblem
  | LineupTooAmbiguousProblem
  | ArtistResolutionProblem
  | MovieTmdbMissProblem
  | MovieLowConfidenceProblem
  | MovieAmbiguousMatchProblem
  | PlayAmbiguousMatchProblem
  | PlayNoImageFoundProblem
  | PersonAmbiguousMatchProblem
  | LowScoreProblem
  | ImageLowConfidenceProblem
  | EnrichmentLowConfidenceProblem
  | EnrichmentCuratorReviewProblem
  | RefreshReviewProblem;

export interface HitlContext {
  reason: HitlReason;
  stage: string;
  /**
   * Widened from the three scrape pipelines to `string`: an enrichment HITL's
   * context carries `content_type: "enrichment_artist"` and
   * the vocabulary has nine more values that are not site content types. Render
   * it through `contentTypeLabel()` from `@/lib/tina/content-types`; every
   * consumer that branches on it must keep a default arm.
   */
  content_type: string;
  stage_outputs: Record<string, Record<string, unknown>>;
  problem: HitlProblem;
}

// Mirror of src/db/schemas/hitl_resolution_input.py — same `kind` strings.
export interface PickVenue {
  kind: "venue.pick";
  venue_id: string;
}
export interface CreateVenueFromMaps {
  kind: "venue.create_from_maps";
  maps_url: string;
  geodata_id?: string;
}
export interface CreateVenueFromCandidate {
  kind: "venue.create_from_candidate";
  geodata_id: string;
}
export interface UseVenueMapsUrl {
  kind: "venue.use_maps_url";
  maps_url: string;
  geodata_id?: string;
}
/**
 * How a curator resolves an accepted `venue` proposal on a refresh review (2026-09-11) — the
 * SAME three options the scrape venue HITL offers, as the same three payloads. Mirror of tina's
 * `RefreshVenueAction`. `venue.create_from_candidate` is absent on purpose: it confirms a Places
 * candidate stashed on a `venue_geodata_miss` park, and a refresh park never carries one.
 */
export type RefreshVenueAction =
  | PickVenue
  | CreateVenueFromMaps
  | UseVenueMapsUrl;
export interface ArtistMemberResolution {
  name: string;
  action: "pick" | "spotify" | "unmatched" | "drop";
  artist_id?: string | null;
  spotify_ref?: string | null;
}
export interface ArtistResolution {
  kind: "artist";
  members: ArtistMemberResolution[];
}
export interface PickMovie {
  kind: "movie.pick";
  movie_id: string;
}
export interface CreateMovieFromTmdb {
  kind: "movie.create_from_tmdb";
  tmdb_ref: string;
}
export interface PickPlay {
  kind: "play.pick";
  play_id: string;
}
export interface CreatePlay {
  kind: "play.create";
  title: string;
  image_url: string;
}
export interface PickPerson {
  kind: "person.pick";
  person_id: string;
}
export interface CreatePersonFromTmdb {
  kind: "person.create_from_tmdb";
  tmdb_ref: string;
}
/**
 * Create a stage person from a name the curator typed — tina's
 * `CreatePersonByName`. The escape hatch for Greek stage actors, who have no TMDb entry:
 * spec decision D1 keeps Tina from minting person docs automatically, and this kind is the
 * one path that overrides that, per credit, by hand.
 *
 * `department` is a free string on both sides; the picker offers tina's current
 * `KnownForDepartment` vocabulary. A missing or null `department` means the curator
 * named no department, and lets tina's stage fall back to the target's known department.
 */
export interface CreatePersonByName {
  kind: "person.create";
  name: string;
  image_url?: string | null;
  department?: string | null;
}
export interface ScoreApprove {
  kind: "score.approve";
}
export interface ApproveImage {
  kind: "image.approve";
}
export interface ReplaceImage {
  kind: "image.replace";
  image_url: string;
}
/**
 * Write the parked proposal back as `Enriched`, verbatim. Carries no data: the
 * DTO to PUT is `hitl_context.problem.proposal.payload`, already on the row.
 */
export interface ApproveEnrichment {
  kind: "enrichment.approve";
}
/**
 * PUT a curator-edited DTO instead of the model's proposal.
 *
 * **Omitting an envelope sibling inherits the parked proposal's**, for all three
 * of them — a replace edits the enrichment, it is not a claim that the movie has
 * no Greek title or the artist no tracks. An explicit `[]`/`{}` still clears.
 */
export interface ReplaceEnrichment {
  kind: "enrichment.replace";
  /** The edited DTO, camelCase, as the backend takes it. A snake_case key is dropped in silence. */
  enrichment: Record<string, unknown>;
  enrichment_sources?: Record<string, unknown>[] | null;
  /** Movie enrichments only. */
  greek_translation?: Record<string, unknown> | null;
  /** Artist enrichments only. */
  track_previews?: Record<string, unknown>[] | null;
}
/** Re-run the enricher from scratch; the gate applies again. Capped at 3 parks per job row. */
export interface RetryEnrichment {
  kind: "enrichment.retry";
}
/**
 * Resolve an `enrichment_curator_review` park — mirror of tina's
 * `EnrichmentCuratorReviewResolution` (artist-field-locks, 2026-09-28).
 *
 * One verdict per held field, keyed by the curated field name (`"about"`,
 * `"enrichment.faq"`, …): `"accept"` writes the automatic value and clears the hold,
 * `"decline"` keeps the curator's value and stamps a fresh `declinedAt`. **A field
 * omitted from `decisions` is left alone** — tina's resolver accepts a partial map, so a
 * curator tool that only sends some of the held fields must not crash the job. IMS's own
 * panel always sends every field; that is this panel's convention, not a wire requirement.
 */
export interface CuratorReviewEnrichment {
  kind: "enrichment.curator_review";
  decisions: Record<string, "accept" | "decline">;
}
/**
 * The twelve change kinds a refresh job can carry — mirror of tina's `ChangeKind`. Ten can be
 * proposed to a curator; `type` and `genres` are only ever written by tina itself after a
 * lineup change, so they appear under "Already applied automatically" and nowhere else.
 *
 * A `const` tuple for the same reason `FINAL_OUTCOMES` is one: the panel's label map is a
 * `Record<RefreshChangeKind, string>`, so a thirteenth kind cannot arrive unlabelled.
 */
export const REFRESH_CHANGE_KINDS = [
  "cancelled",
  "date",
  "venue",
  "title",
  "description",
  "ticket_url",
  "poster",
  "lineup",
  "tag_add",
  "tag_remove",
  "type",
  "genres",
] as const;
export type RefreshChangeKind = (typeof REFRESH_CHANGE_KINDS)[number];

/** Which entry point seeded the job: the nightly sweep, or the duplicate hook. */
export const REFRESH_MODES = ["scheduled", "dup"] as const;
export type RefreshMode = (typeof REFRESH_MODES)[number];

/** What to do about an event whose page is gone. Mirror of tina's `page_gone_action`. */
export const REFRESH_PAGE_GONE_ACTIONS = [
  "cancel",
  "retire",
  "dismiss",
] as const;
export type RefreshPageGoneAction = (typeof REFRESH_PAGE_GONE_ACTIONS)[number];

/**
 * Why tina called the page gone — the seven codes it stamps on `page_gone_reason`.
 *
 * Five come off the fetch stage's strike counter (`stages/fetch.py::_strike`): `bad_fetch`,
 * `not_listed`, `unreadable` (R104 — no reader could read the page for four nights),
 * `blocked` (R106 — every reader, a real browser included, met a bot or permission wall)
 * and `redirected` — the page's own link now serves a listing page, so tina never actually
 * read an event page to compare against. Two come off the compare model's verdict
 * (`stages/compare.py`: `focal_missing` when it read the page but could not find the
 * published instance on it, `same_event_false` when it found the instance and still said
 * the page is about something else).
 *
 * A `const` tuple, and the panel's label map is a `Record<RefreshPageGoneReason, string>`,
 * for the same reason `REFRESH_CHANGE_KINDS` is one — a new code cannot arrive
 * unlabelled. It is still rendered through a lookup that falls back to the raw string,
 * because this is wire data from a deploy that may be newer than this build.
 */
export const REFRESH_PAGE_GONE_REASONS = [
  "bad_fetch",
  "not_listed",
  "unreadable",
  "blocked",
  "redirected",
  "focal_missing",
  "same_event_false",
] as const;
export type RefreshPageGoneReason = (typeof REFRESH_PAGE_GONE_REASONS)[number];

/**
 * One curator verdict on one performer inside a `lineup` proposal — mirror of tina's
 * per-name decision entry.
 *
 * `action` says which side of the diff the name came from, and is not inferable from
 * `name` alone: `refresh_apply` needs to know whether an unticked row is "do not add this"
 * or "do not remove this", and the two are opposites.
 */
export interface RefreshLineupNameDecision {
  name: string;
  action: "add" | "remove";
  accept: boolean;
}

/** One curator verdict on one proposal. Mirror of tina's `RefreshChangeDecision`. */
export interface RefreshChangeDecision {
  kind: RefreshChangeKind;
  accept: boolean;
  /**
   * A curator edit of the proposed value, or — for `kind: "venue"` — **the chosen venue id**,
   * which tina requires: `refresh_apply` resolves the venue by id to build the PATCH's venue DTO,
   * geodata, geolocation and timezone, and an accepted venue decision without one cannot be
   * written. Omit to accept `proposal.new` verbatim.
   */
  value?: string | null;
  /**
   * `kind: "lineup"` only — per-performer verdicts, and the ONLY way a refresh can remove a
   * performer from a published event.
   *
   * **The list is exhaustive when it is non-empty, and that is the whole contract:**
   *
   * - **Empty or absent** — the legacy path. Tina applies every addition the model proposed
   *   and removes nobody. This is what an old IMS build sends, and it is why a plain accept
   *   could never delete a performer.
   * - **Non-empty** — tina applies exactly the `accept: true` entries and nothing else. An
   *   addition that is *missing* from a non-empty list is **not applied**.
   *
   * So a client that sends this at all must send **one entry per delta** — every addition
   * and every removal in {@link RefreshLineupDiff}, each carrying its true tick state.
   * Sending only the ticked rows would silently drop the unticked additions, which is worse
   * than the legacy behaviour it was meant to improve on. `buildRefreshDecisions` is the one
   * place that builds this, and it builds it from the diff, not from the ticks.
   */
  lineup?: RefreshLineupNameDecision[];
  /**
   * `kind: "venue"` only — the curator's venue action (2026-09-11): pick an existing venue,
   * create one from a Google Maps link, or use the link as a location-only pin. Mirror of tina's
   * `RefreshChangeDecision.venue`. A pick ALSO sets `value` to the id, so a tina that predates
   * this field still reads the decision as before.
   */
  venue?: RefreshVenueAction;
}
/**
 * Resolve a `refresh_review` park — mirror of `RefreshReviewResolution`.
 *
 * `kind` is the dotted `"refresh.review"`, not the bare reason name: every other variant in
 * tina's `HitlResolutionInput` uses the `entity.verb` convention, and this one follows it.
 *
 * `decisions` and `page_gone_action` are independent, not alternatives — a page-gone park has no
 * proposals and so sends `[]`, but the schema permits both.
 */
export interface RefreshReviewResolution {
  kind: "refresh.review";
  decisions: RefreshChangeDecision[];
  page_gone_action?: RefreshPageGoneAction | null;
  duplicate_action?: RefreshDuplicateAction | null;
}
export type HitlResolutionInput =
  | PickVenue
  | CreateVenueFromMaps
  | CreateVenueFromCandidate
  | UseVenueMapsUrl
  | ArtistResolution
  | PickMovie
  | CreateMovieFromTmdb
  | PickPlay
  | CreatePlay
  | PickPerson
  | CreatePersonFromTmdb
  | CreatePersonByName
  | ScoreApprove
  | ApproveImage
  | ReplaceImage
  | ApproveEnrichment
  | ReplaceEnrichment
  | RetryEnrichment
  | CuratorReviewEnrichment
  | RefreshReviewResolution;

// Wire payload at /events. Renamed in IMS to PipelineItem to avoid
// confusion with the IMS `events` resource.
/**
 * Per-discover-seed breakdown of found URLs into new vs already-known,
 * persisted by the worker after the dedup pass (tina events.discover_summary).
 * Mirrors the discover-test DiscoverTestReport counts so the item panel can
 * show the same breakdown. Null on non-discover items and pre-column seeds.
 */
export interface DiscoverSummary {
  discovered_count: number;
  new_count: number;
  known_count: number | null;
  queued_count: number;
  checked_known: boolean;
  new_urls: string[];
  known_urls: string[];
  truncated: boolean;
}

export interface PipelineItem {
  id: string;
  run_id: string;
  run_trigger_type?: string | null;
  run_triggered_by?: string | null;
  run_created_at?: string | null;
  parent_event_id: string | null;
  discovered_count?: number;
  discover_summary?: DiscoverSummary | null;
  coalesced_count?: number;
  coalesced_into_event_id?: string | null;
  group_id: string | null;
  is_primary: boolean;
  depends_on_event_id: string | null;
  inheritance_flags: InheritanceFlags | null;
  site: string;
  content_type: string;
  url: string | null;
  status: ItemStatus;
  current_stage: string | null;
  final_outcome: FinalOutcome | null;
  final_confidence: string | null;
  hitl_reason: HitlReason | null;
  hitl_context: HitlContext | null;
  hitl_resolution: HitlResolutionInput | null;
  /**
   * Who resolved this item's HITL park — the curator's Firebase uid — and when.
   * Tina stamps both in `resolve_hitl`; `EventRead` exposes them.
   *
   * **NULL means "unknown", not "never parked".** A park resolved before
   * migration `0027` was never backfilled, so a missing uid is not evidence the
   * item went through review. Read `hitl_reason`/`hitl_resolution` for that
   * question.
   */
  hitl_resolved_by?: string | null;
  hitl_resolved_at?: string | null;
  skipped_reason: string | null;
  created_event_id: string | null;
  created_entities: Record<string, unknown> | null;
  error: Record<string, unknown> | null;
  attempt_count: number;
  claimed_by: string | null;
  claimed_at: string | null;
  last_heartbeat_at: string | null;
  created_at: string;
  finished_at: string | null;
  metadata: Record<string, unknown> | null;
}

export interface StageExecutionRead {
  id: string;
  event_id: string;
  run_id: string;
  stage: string;
  attempt: number;
  status: StageExecutionStatus;
  started_at: string | null;
  finished_at: string | null;
  duration_ms: number | null;
  prompt_version_id: string | null;
  model: string | null;
  input_ref: string | null;
  output_ref: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  thinking_tokens: number | null;
  cost: string | null;
  output_confidence: string | null;
  confidence_delta: string | null;
  error: Record<string, unknown> | null;
  worker_id: string | null;
}

export interface StageArtifactRead {
  name: string;
  ref: string;
  kind: "input" | "output" | "artifact";
  content_type: string | null;
  size_bytes: number | null;
  format: "json" | "text" | "binary" | "error";
  content: unknown;
  truncated: boolean;
  error: string | null;
}

/**
 * What approving a `low_score` HITL would cascade to — `GET /hitl/{id}/vouch-preview`.
 *
 * Approving one low-score event also confirms its venue, and Tina then releases every
 * sibling parked behind that same venue penalty which the credit clears. IMS cannot work
 * the number out itself: both the venue key hash and the release predicate live in Tina.
 *
 * `applies: false` (with zero counts) is the normal answer for any HITL a vouch cascades
 * nothing from — another reason, no dedup key, or the kill-switch off.
 *
 * `releasable_count` excludes the previewed event, so it reads as "N *others*".
 * `held_count` is the siblings a vouch would NOT free; it is worth showing because it is
 * the evidence the cascade is not a blanket approval.
 *
 * A snapshot, never a contract: another curator resolving, or a fresh run parking more
 * events, moves these between render and click.
 */
export interface VouchPreview {
  applies: boolean;
  entity: string | null;
  entity_name: string | null;
  releasable_count: number;
  held_count: number;
  already_confirmed: boolean;
}

export interface StageArtifactsRead {
  stage_execution_id: string;
  event_id: string;
  run_id: string;
  stage: string;
  attempt: number;
  prefix: string;
  list_error: string | null;
  artifacts: StageArtifactRead[];
}

export interface RunConfig {
  prompt_overrides?: Record<string, { prompt_version_id: string }>;
  score_threshold?: number | null;
  cost_cap_usd?: string | null;
  dry_run?: boolean;
  [key: string]: unknown;
}

/**
 * Every value `v_refresh_jobs.refresh_outcome` can take — mirror of tina's `RefreshOutcome`
 * Literal (`src/db/queries/refresh_state.py`), which is also `record_refresh_outcome`'s input.
 *
 * **Not `FinalOutcome` with the prefix stripped.** The view derives this column in SQL from
 * `status`/`skipped_reason`/`final_outcome`, because the stages' own `metadata.refresh_outcome`
 * is an in-flight breadcrumb: `refresh_diff` writes `"changed"` there mid-walk, and a failed job
 * never gets it written at all. So `bad_fetch`, `not_listed`, `hitl` and `failed` are outcomes
 * here with no `FinalOutcome` member behind them, and `refresh_updated` reads back as `updated`.
 *
 * An unknown value is a **422** from `GET /refresh-jobs?refresh_outcome=`, not an empty page, so
 * this list has to stay in step with the view.
 */
export const REFRESH_OUTCOMES = [
  "unchanged",
  "updated",
  "dismissed",
  "shadow",
  "retired",
  "bad_fetch",
  "not_listed",
  "skipped",
  "failed",
  "hitl",
] as const;
export type RefreshOutcome = (typeof REFRESH_OUTCOMES)[number];

/**
 * One row of `GET /refresh-jobs` — tina's `RefreshJobViewRow` over `v_refresh_jobs`.
 *
 * A refresh job is an ordinary `events` row (`content_type = 'music_event_refresh'`), so `id` is
 * an item id and drills into the item show page like any other row. The columns past
 * `hitl_reason` are jsonb projections, hence nullable — except the four the view `COALESCE`s
 * (`not_listed`, `proposal_count`, `applied_count`, `page_gone`), which are never null.
 *
 * `cost` is a decimal string, like every other money field off this API.
 */
export interface RefreshJobRow {
  id: string;
  run_id: string;
  status: ItemStatus;
  site: string;
  url: string | null;
  current_stage: string | null;
  final_outcome: FinalOutcome | null;
  skipped_reason: string | null;
  hitl_reason: HitlReason | null;
  mode: RefreshMode | null;
  /** The published Mood event this job refreshes. Null only on a malformed row. */
  admin_event_id: string | null;
  /**
   * The published event's own NAME — tina's ruling R77, off
   * `hitl_context.stage_outputs.refresh_compare.published.name`.
   *
   * **Optional, not just nullable**, and the difference is load-bearing: tina exposes the column
   * from migration `0061` onward, so this build has to read correctly against an orchestrator that
   * predates it. Absent means "this deployment does not have the column"; `null` means "this job
   * never parked, so there is no published view". Both fall back to `admin_event_id`, which is
   * exactly what the list showed before R77.
   */
  published_name?: string | null;
  not_listed: boolean;
  refresh_outcome: RefreshOutcome | null;
  proposal_count: number;
  applied_count: number;
  /**
   * The Mood field names this refresh actually wrote, e.g.
   * `["media", "hasPlaceholderArt", "lineup"]`, and `[]` when it wrote nothing.
   *
   * **Which fields, where `applied_count` only says how many.** The event edit
   * page's "Refresh traces" card renders one chip per entry, so a curator can see
   * *what* a refresh changed without opening the job — a count alone would send
   * them to the item page for every row.
   *
   * Optional for the same pre-deploy reason as `published_name` and
   * `review_kinds`: tina exposes the column from this change onward, so this
   * build has to read correctly against an orchestrator that predates it. Absent
   * means "this deployment does not have the column"; `[]` means "this refresh
   * changed nothing". The two are not the same answer and a reader must not fold
   * them together.
   */
  applied_fields?: string[];
  /**
   * The change kinds still awaiting a curator's decision — `hitl_context.problem.proposals`,
   * deduped and sorted by tina.
   *
   * **Not the array `proposal_count` counts.** That one is `metadata.refresh.proposals`, the full
   * record of what the compare stage found, which also names the kinds `refresh_apply` already
   * wrote by itself. The two disagree on roughly 9% of parked rows, so this is the one a review
   * list may render. `[]` when nothing is open, which on live data means the park is about the page
   * itself having gone away. Optional for the same pre-deploy reason as `published_name` — absent
   * is "no such column here", distinct from an empty array.
   *
   * Typed `string[]` rather than `RefreshChangeKind[]` on purpose: it is a jsonb array off the
   * wire, and a kind this build has no label for must render as itself, not crash a lookup. Read it
   * through `refreshChangeKindLabel`.
   */
  review_kinds?: string[];
  page_gone: boolean;
  page_gone_reason: string | null;
  cost: string;
  created_at: string;
  finished_at: string | null;
}

/** One bucket of the outcome histogram. `outcome: null` is "still in flight", not "unknown". */
export interface RefreshOutcomeCount {
  outcome: RefreshOutcome | null;
  count: number;
}

/**
 * `GET /refresh-jobs/summary` — the tile row over one refresh run.
 *
 * Server-side aggregation, unlike the enrichment run page's client-side count over every row:
 * that is safe there (a p95 of 2 items per run) and not here, where one sweep hour seeds
 * thousands. `v_run_summary` cannot answer it either — its counters key on `created`/`duplicate`,
 * outcomes no refresh job carries.
 */
export interface RefreshJobsSummary {
  total: number;
  outcomes: RefreshOutcomeCount[];
  page_gone_count: number;
  proposal_count: number;
  applied_count: number;
  cost: string;
}

export interface OverviewRow {
  since: string;
  until: string;
  total_events: number;
  created: number;
  manual_review: number;
  duplicate: number;
  skipped: number;
  failed: number;
  total_cost: string | null;
  avg_confidence: string | null;
}

/**
 * One row of `GET /insights/stages`, per (day, stage, prompt_version_id).
 *
 * `success_rate` was removed by Tina migration `0057`: it was
 * `ok_count / executions`, which scored `inherited`, `skipped`, `duplicate`,
 * `hitl` and `deferred` — five intended outcomes — as failures. `fail_rate` is
 * the honest headline and `ok_rate` is the old number under a name that does
 * not claim to be health.
 *
 * The seven `*_count` fields partition `executions` exactly. The five non-`ok`
 * ones plus both rates are **optional** so this client also renders correctly
 * against an API deployed before `0057`; `fail_count`, `ok_count` and
 * `executions` have always been present, which is why the failure headline
 * needs no fallback.
 *
 * `p50_ms`/`p95_ms`/`p99_ms`/`avg_tokens`/`avg_cost` cover only executions that
 * actually ran — `0057` excludes `inherited` rows, which Tina stores as literal
 * zeros for duration, tokens and cost. They are null for a group in which
 * nothing but inheritance happened.
 *
 * `recovered_fail_count` (Tina migration `0077`) is the part of `fail_count`
 * that a later attempt of the same (event, stage) fixed. It is a subset of
 * `fail_count`, not a partition bucket. Optional, so an older API reads as
 * "nothing recovered".
 */
export interface StagePerfDailyRow {
  day: string;
  stage: string;
  prompt_version_id: string | null;
  executions: number;
  ok_count: number;
  fail_count: number;
  recovered_fail_count?: number;
  inherited_count?: number;
  skipped_count?: number;
  duplicate_count?: number;
  hitl_count?: number;
  deferred_count?: number;
  fail_rate?: string | null;
  ok_rate?: string | null;
  p50_ms: number | null;
  p95_ms: number | null;
  p99_ms: number | null;
  avg_tokens: string | null;
  avg_cost: string | null;
}

export interface CostDailyRow {
  day: string;
  stage: string;
  content_type: string;
  total_cost: string | null;
  llm_cost: string | null;
  api_cost: string | null;
  input_tokens: number;
  output_tokens: number;
  thinking_tokens: number;
}

/**
 * One row of tina `GET /insights/fixed-cost` — the per-day share of a fixed
 * cost: Cloud Run from the GCP billing export, or a subscription expanded to
 * one part per day. Several rows per day, one per `item`; the spend chart sums
 * them per `category`. `day` is a UTC date, the same convention as
 * `CostDailyRow.day`. `cost_usd` is a decimal serialized as a string.
 */
export interface FixedCostDailyRow {
  day: string;
  category: "cloud_run" | "subscription";
  item: string;
  cost_usd: string;
}

export interface EventsDailyRow {
  day: string;
  content_type: string;
  events_processed: number;
}

export interface SiteQualityRow {
  site: string;
  event_count: number;
  created_count: number;
  hitl_count: number;
  created_rate: string | null;
  manual_review_rate: string | null;
  avg_confidence: string | null;
}

/**
 * One row of `GET /insights/errors`, per (day, stage, error_type, recovered).
 *
 * `recovered` (Tina migration `0077`) is true when a later attempt of the same
 * (event, stage) did not fail — the retry fixed it. So a (day, stage,
 * error_type) can come as two rows. Optional, so an older API reads as
 * "not recovered", which is how the page behaved before.
 */
export interface ErrorBucketsRow {
  day: string;
  stage: string;
  error_type: string;
  recovered?: boolean;
  error_count: number;
  /** Distinct sites behind the bucket (tina migration 0078). Absent on an older API. */
  site_count?: number;
  sample_event_ids: string[];
}

export interface HitlBacklogRow {
  hitl_reason: HitlReason;
  pending_count: number;
  oldest_at: string | null;
  newest_at: string | null;
}

export interface HitlDominationRow {
  country_code: string;
  resolved_by: string;
  /**
   * Resolve ACTIONS, not rows in tina's `events`: since tina's `0074` a force-closed
   * coalesced cluster counts once, not once per sibling it stamped.
   */
  resolve_count: number;
  first_resolved_at: string;
  last_resolved_at: string;
  /** Newest resolve day, `YYYY-MM-DD` in the ops timezone (Europe/Athens). */
  last_resolved_day: string;
  /** Resolves on `last_resolved_day`. Only "today" when that day IS today. */
  resolved_on_last_day: number;
}

/**
 * One curator's HITL working rhythm. Mirror of tina's `mv_curator_activity`.
 *
 * Grain is the curator ALONE, unlike {@link HitlDominationRow}'s `(country_code,
 * resolved_by)` — a pace is one number per person, so a curator working in nine
 * countries still has one pace. The board merges the two on uid.
 */
export interface CuratorActivityRow {
  resolved_by: string;
  /** Days with 10 or more resolves — the divisor the pace is taken over. */
  active_days: number;
  total_resolves: number;
  /**
   * Mean resolves on a day with 10 or more. `null` when the curator never had such a
   * day — a row they can still have, since the 10-resolve life floor and the 10-resolve
   * day floor are separate tests.
   */
  avg_day: number | null;
  /** Resolves in the trailing 30 days. */
  last_30d: number;
  /** Biggest single day, over EVERY day — a maximum needs no floor. */
  best_day: number;
  /** The Athens date `best_day` was set, `YYYY-MM-DD`. */
  best_day_date: string;
}

// One enabled site of an enabled site group: what the cron runs on its schedule.
// Twin of tina `ScheduledCoverageRow` (`GET /insights/scheduled-coverage`).
export interface ScheduledCoverageRow {
  country_code: string | null;
  city: string | null;
  content_type: string;
  site: string;
  // The Instagram handle the site is scraped under, or null for a website/API
  // source. Absent from orchestrators older than this field.
  instagram_handle?: string | null;
}

export interface TodayCoverageRow {
  country_code: string | null;
  city: string | null;
  // The site's vertical (music_event, cinema_screening, …). Null when the site no
  // longer resolves; absent from orchestrators older than this field.
  content_type?: string | null;
  site: string;
  triggered_by: string;
}

// Sites — assumed shape pending backend (spec §6.1).
export interface Site {
  name: string;
  region?: string | null;
  city?: string | null;
  enabled?: boolean;
  cadence_minutes?: number | null;
  last_processed_at?: string | null;
}

export interface RunTriggerResponse {
  run_id: string;
  seeded: { site: string; seed_event_id: string }[];
}

export interface TagRuleRead {
  id: string;
  tag_name: string;
  explanation: string;
  enabled: boolean;
  created_at: string;
  created_by: string | null;
  updated_at: string;
  updated_by: string | null;
}

export interface NotificationRuleRead {
  id: string;
  verdict: string;
  value: string;
  explanation: string;
  enabled: boolean;
  created_at: string;
  created_by: string | null;
  updated_at: string;
  updated_by: string | null;
}

export type TinaDiscoverTestStatus = "pending" | "running" | "done" | "failed";

export interface SiteRead {
  name: string;
  content_type: string;
  x_strategy: string;
  country_code: string;
  city: string | null;
  config: Record<string, unknown>;
  enabled: boolean;
  source: string;
  cadence_minutes: number | null;
  last_processed_at: string | null;
  created_at: string;
  created_by: string | null;
  updated_at: string;
  updated_by: string | null;
}

export interface SiteUpdatePayload {
  enabled?: boolean;
  cadence_minutes?: number | null;
  config?: Record<string, unknown>;
}

/**
 * POST /sites body as the Instagram create form sends it. `content_type`,
 * `x_strategy` and `source` are literals because the form fixes them: the
 * pipeline registers instagram stages solely on the music_event pipeline, and
 * `"manual"` is the source every hand-added row carries, so source filters keep
 * working.
 *
 * `site_groups` (group ids written as memberships in the same transaction) is
 * optional and must be **omitted** rather than sent empty: tina's `SiteCreate`
 * is `extra="forbid"`, so a deployment that predates the field rejects the key
 * outright. Naming a group therefore requires the newer backend; a group-less
 * create works against either.
 */
export interface InstagramSiteCreatePayload {
  name: string;
  content_type: "music_event";
  x_strategy: "instagram";
  country_code: string;
  city: string;
  config: { instagram_handle: string };
  enabled: boolean;
  source: "manual";
  site_groups?: string[];
}

export interface DiscoverTestReport {
  site: string;
  strategy: string;
  content_type: string;
  status: string;
  source: string;
  discovered_count: number;
  known_count: number | null;
  new_count: number;
  checked_known: boolean;
  discovered_urls: string[];
  known_urls: string[];
  new_urls: string[];
  output?: unknown;
  metadata?: Record<string, unknown>;
  error?: Record<string, unknown> | null;
}

export interface DiscoverTestRead {
  id: string;
  site: string;
  status: TinaDiscoverTestStatus;
  browser_backend: string | null;
  report: DiscoverTestReport | null;
  error: Record<string, unknown> | null;
  requested_by: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

export interface DiscoverTestCreateResponse {
  id: string;
  status: TinaDiscoverTestStatus;
}

export type ManualUnblockMode = "recaptcha" | "login";

export interface UnblockSessionRead {
  session_id: string;
  live_view_url: string;
  mode: ManualUnblockMode;
  context_id: string | null;
}

export interface TinaSiteGroup {
  id: string;
  name: string;
  time_of_day: string; // "HH:MM:SS" in the fixed ops timezone (Europe/Athens)
  days_of_week: number[]; // ISO 1=Mon … 7=Sun
  enabled: boolean;
  last_fired_at: string | null;
  sites: string[];
  member_count: number;
  created_at: string;
  created_by: string | null;
  updated_at: string;
  updated_by: string | null;
}

export interface SiteGroupCreatePayload {
  name: string;
  time_of_day: string; // "HH:MM" or "HH:MM:SS"
  days_of_week: number[];
  sites: string[];
  enabled: boolean;
  updated_by?: string | null;
}

export interface SiteGroupUpdatePayload {
  name?: string;
  time_of_day?: string;
  days_of_week?: number[];
  sites?: string[];
  enabled?: boolean;
  updated_by?: string | null;
}

/**
 * The nine venue-cache-audit checks — mirror of tina's `FindingKind`
 * (`src/audit/venue/models.py`) and of the `FindingKindName` Literal the route validates
 * `kind` against.
 *
 * A `const` tuple, not a bare union, for the same reason `FINAL_OUTCOMES` is one: the
 * page's kind filter and its per-kind count strip both enumerate this array, and the
 * label/hint maps are `Record<VenueAuditKind, string>` — so a tenth check cannot arrive
 * unlabelled or unfilterable.
 *
 * A different seventh, `locality_mismatch`, was designed and cut on 2026-09-08 before it
 * shipped: a coordinate test could not see either venue it was built for, because both
 * carry their city centre's coordinates rather than their address's, and it flagged 1,182
 * venues doing it. `GET /venue-audit/findings?kind=locality_mismatch` is a 422, so listing
 * it here would hand the curator a filter that empties the page.
 *
 * `cid_drift`, `venue_named_by_address` and `duplicate_name_and_address` are the three
 * checks that followed the original six. `duplicate_google_place` and `place_not_venue`
 * are the tenth and eleventh, both added on 2026-09-09; `venue_twin` is the twelfth,
 * added on 2026-09-29.
 *
 * `duplicate_google_place` shipped in Tina before it was listed here, so for a day the
 * grid rendered its raw kind string. That degraded gracefully — `venueAuditKindLabel`
 * falls back to the kind — but it was still a kind the filter dropdown could not select,
 * which is exactly what this list exists to prevent.
 */
export const VENUE_AUDIT_KINDS = [
  "placeholder_fan_in",
  "cid_collision",
  "cid_self_inconsistent",
  "merged_cid_overlap",
  "non_venue_type",
  "venue_missing",
  "cid_drift",
  "venue_named_by_address",
  "duplicate_name_and_address",
  "duplicate_google_place",
  "place_not_venue",
  "venue_twin",
] as const;
export type VenueAuditKind = (typeof VENUE_AUDIT_KINDS)[number];

/**
 * Five kinds can be `low`: `cid_collision` and `venue_twin` derive severity from a
 * distance, and `duplicate_name_and_address`, `duplicate_google_place` and
 * `place_not_venue` are ALWAYS `low`. The first two of those three because a duplicate
 * pair still lands every event on the right building — the repair is a merge, not a
 * rebind. `place_not_venue` because nobody is misdirected at all: the coordinates are
 * right, and the only question is whether a village square deserves a venue page.
 *
 * `venue_twin` is the reverse of `cid_collision`: `high` within 50 m, `low` from 50 to
 * 150 m. A user sees the same concert twice, so the close pairs come first; further out
 * the sample was mostly real neighbours.
 *
 * The other seven are `high` by construction — a document contradicting its own place id,
 * a merge claiming another venue's place, a catch-all venue, a dead reference, a non-venue
 * type, a CID that drifted off its own venue, and a venue named after its own street
 * address are each wrong however close anything is.
 */
export const VENUE_AUDIT_SEVERITIES = ["high", "low"] as const;
export type VenueAuditSeverity = (typeof VENUE_AUDIT_SEVERITIES)[number];

/** The four views `GET /venue-audit/findings` serves; `open` is its default. */
export const VENUE_AUDIT_STATUSES = [
  "open",
  "dismissed",
  "resolved",
  "all",
] as const;
export type VenueAuditStatus = (typeof VENUE_AUDIT_STATUSES)[number];

/** One venue inside a finding — tina's `EvidenceVenue`, display fields only. */
export interface VenueAuditEvidenceVenue {
  venue_id: string;
  /** Blank when the catalog no longer holds the venue — which IS `venue_missing`. */
  name: string;
  address: string;
  lat: number | null;
  lng: number | null;
  cid_role: "primary" | "merged" | "none";
  place_type: string;
}

/** One published event inside a `venue_twin` pair — tina's `TwinEventSide`. */
export interface VenueAuditTwinEventSide {
  /** The Mood event document id, what `/events/edit/:id` takes. */
  mood_event_id: string;
  venue_id: string;
  title: string;
  /** ISO datetime. */
  start: string;
  /** The Tina site that published it. */
  site: string;
}

/**
 * The same event published once at each venue of a `venue_twin` finding — tina's
 * `TwinEventPair`. `a` is the event at the lower venue id, `b` at the higher; `match` says
 * which leg of the rule fired, so a title-only pair can be weighed for what it is.
 */
export interface VenueAuditTwinEventPair {
  a: VenueAuditTwinEventSide;
  b: VenueAuditTwinEventSide;
  match: "shared_artist" | "same_title";
}

/**
 * The `evidence` jsonb — tina's `FindingEvidence`, passed through verbatim by the route.
 *
 * Every field is optional because each kind fills its own subset, and because the API
 * types this as an open `dict[str, Any]` on purpose: a new evidence field must not need a
 * coordinated IMS deploy. So read every field defensively.
 *
 * `site_city`, `site_country` and `distance_from_site_km` are deliberately ABSENT. The
 * first two exist on tina's model but no shipping check populates them; the third was
 * deleted with `locality_mismatch`. Typing a field the server never sends is how a cell
 * starts rendering "undefined km" and a curator starts believing it.
 */
export interface VenueAuditEvidence {
  venues?: VenueAuditEvidenceVenue[];
  /**
   * The Google CID the finding is keyed on — the two original CID checks, the merge
   * overlap, and `cid_drift`'s own drifted CID: the value the cache still remembers, no
   * longer on the venue as primary or merged.
   */
  cid?: string | null;
  /**
   * `cid_drift` only: the venue's CURRENT CIDs — its primary plus every merged one — so
   * the finding can show what moved. `cid` above is the stale value the cache still
   * carries; this is what the venue actually holds today.
   */
  current_cids?: string[];
  /** Widest pairwise gap between the venues, in metres. Null below two located venues. */
  max_distance_m?: number | null;
  /**
   * A human-confirmed cache row for this CID points at a different venue than an
   * unconfirmed one. Evidence only — tina never treats a confirmation as an exemption,
   * because 104 of the 121 worst rows in the catalog were picked by a human.
   */
  human_confirmed_disagreement?: boolean;
  decoded_cid_from_place_id?: string | null;
  stored_cid?: string | null;
  /** Reach: how much of tina's venue cache is bound to this venue. */
  cache_rows?: number;
  distinct_cache_keys?: number;
  distinct_sites?: number;
  /**
   * Published Mood events already bound to this venue — the number that turns "17 cache
   * rows" into "796 events inherited a wrong venue".
   *
   * Set only on the kinds keyed to a single venue; the kinds that name several venues —
   * the two original CID-keyed ones, and `duplicate_name_and_address` — cannot attribute
   * one count to any of them, so they send 0 and the Reach column shows a dash.
   * A FLOOR rather than a total: tina counts events whose `metadata.resolved_venue_id`
   * is this venue, and about 15% of published events never got that field written.
   */
  published_events?: number;
  /**
   * `place_not_venue` only: how many Mood `events` documents name this venue, ever.
   *
   * A different population from `published_events`, not a duplicate of it, and the gap is
   * why the check exists. `published_events` counts tina's OWN rows; this counts
   * Firestore. `Telekom Center Athens` holds 11 Mood events and 0 tina ones, because the
   * Mood integration created both the venue and its events.
   *
   * Absent means the count was not recorded, never that it was zero — the check skips a
   * venue it could not count.
   */
  mood_event_count?: number | null;
  /**
   * `venue_twin` only: every matching event pair in the 30-day window — the finding's reach
   * number, since a pair-keyed kind sends `published_events: 0`. Absent means an older
   * finding, never an observed zero.
   */
  twin_event_pair_count?: number;
  /** `venue_twin` only: at most five of those pairs, most recent start first. */
  twin_event_pairs?: VenueAuditTwinEventPair[];
  /** The placeholder spellings that collapsed onto one venue. */
  name_variants?: string[];
  /** Named only when every cache row agrees on one site, else null. */
  site?: string | null;
}

/** One row of `GET /venue-audit/findings` — tina's `VenueAuditFindingRead`. */
export interface VenueAuditFindingRow {
  id: string;
  /**
   * Typed as the union but read as wire data: the orchestrator can deploy a seventh check
   * before this build knows it, so every render path falls back rather than assuming
   * exhaustiveness.
   */
  kind: VenueAuditKind;
  severity: VenueAuditSeverity;
  /** `kind:key` — stable identity, and all an unknown kind can be shown by. */
  fingerprint: string;
  evidence: VenueAuditEvidence;
  first_seen_at: string;
  last_seen_at: string;
  dismissed_at: string | null;
  /** The curator's Firebase uid, as IMS sent it. */
  dismissed_by: string | null;
  dismissed_note: string | null;
  /** Set by the audit itself once the finding's evidence stopped existing. */
  resolved_at: string | null;
}

/** Body of `POST /venue-audit/findings/{id}/dismiss`. */
export interface VenueAuditDismissPayload {
  /** Must be a Firebase uid: the API authenticates the service, not the person. */
  dismissed_by: string;
  note?: string | null;
}

/**
 * What `POST /venue-audit/run` reports — an EXECUTION, not an audit outcome.
 *
 * The route starts tina's `tina-venue-audit` Cloud Run Job and returns 202 immediately.
 * It used to hold one request across a ~14,600-document Firestore read plus six checks
 * plus the upsert, under a 60s Cloud Run request timeout — and a Cloud Run timeout is a
 * request *cancellation*, which escaped every error guard on the server, so the symptom
 * was a bare 504 with no findings and no log line.
 *
 * So the counts this used to carry — `findings_total`, `by_kind`, `coverage`,
 * `skipped_short_catalog` — are gone, because a trigger cannot know them. They live in
 * the Job execution's own logs and, durably, in tina's `venue_audit_state`. The page says
 * so rather than showing a stale or invented number, and the findings table below it is
 * where the result actually shows up once the run finishes.
 */
export interface VenueAuditRunResponse {
  /**
   * `dispatched` — the execution was created. `error` — Cloud Run refused; read
   * `error_message`. `noop` — the Job is not configured, which is local dev and CI.
   */
  kind: "noop" | "dispatched" | "error";
  /** Cloud Run execution resource name, when Cloud Run returned one. */
  execution_name?: string | null;
  error_message?: string | null;
}

// ---- Artist duplicate auditor ------------------------------------------------------
//
// tina's `GET /artist-audit/findings`. Same shape and the same discipline as the
// venue-audit block above: the kind list is a const tuple so the label/hint maps in
// `@/lib/tina/artist-audit` are `Record<ArtistAuditKind, string>` and a fifth check
// cannot arrive unlabelled. Spec: tina docs/superpowers/specs/2026-09-15-artist-duplicate-auditor-design.md.

export const ARTIST_AUDIT_KINDS = [
  "duplicate_spotify_id",
  "duplicate_name",
  "merge_state_broken",
  "merged_artist_on_live_lineup",
  "deceased_artist_with_future_events",
] as const;
export type ArtistAuditKind = (typeof ARTIST_AUDIT_KINDS)[number];

export const ARTIST_AUDIT_SEVERITIES = ["high", "low"] as const;
export type ArtistAuditSeverity = (typeof ARTIST_AUDIT_SEVERITIES)[number];

export const ARTIST_AUDIT_STATUSES = [
  "open",
  "dismissed",
  "approved",
  "resolved",
  "all",
] as const;
export type ArtistAuditStatus = (typeof ARTIST_AUDIT_STATUSES)[number];

/** One artist as it appears inside a finding — tina's `EvidenceArtist`. */
export interface ArtistAuditEvidenceArtist {
  artist_id: string;
  name: string;
  name_normalized: string;
  /** Empty string when the document carries no Spotify id. */
  spotify_id: string;
  followers: number;
  /** Live Mood events this artist is billed on. 0 when unknown — read `event_counts_known`. */
  live_events: number;
  live_venues: number;
  created_by: string;
  /** The Tina site that created it, or null for the 56,619 documents Tina did not. */
  tina_site: string | null;
  merged_into: string | null;
  /** ISO timestamp the artist document was created. */
  created_at?: string | null;
  /** Spotify ids absorbed from artists merged INTO this one — evidence a merge already happened here. */
  other_spotify_ids?: string[];
  /** First and last live Mood event this artist is billed on, ISO timestamps. Null when unbooked or unread. */
  first_live_at?: string | null;
  last_live_at?: string | null;
}

/** One future live event on a `deceased_artist_with_future_events` card — tina's `EvidenceEvent`. */
export interface ArtistAuditEvidenceEvent {
  event_id: string;
  name: string;
  /** ISO timestamp. */
  start_at: string | null;
}

/**
 * tina's `FindingEvidence`, snake_case on the wire. Every field optional; each kind fills
 * its own. The card reads ONLY this — opening a finding costs no artist fetch.
 */
export interface ArtistAuditEvidence {
  artists?: ArtistAuditEvidenceArtist[];
  /** `duplicate_spotify_id`: the id both documents carry. */
  shared_spotify_id?: string | null;
  /** `duplicate_name`: live venues both sides have played. A hint, never a gate. */
  shared_venue_count?: number;
  /** `duplicate_name`: the token count that decided severity. */
  name_tokens?: number;
  both_sides_booked?: boolean;
  /**
   * `duplicate_name`: false when either side's bookings could not be read. The zeroes on
   * such a card are UNKNOWNS, and the page must say so rather than let a curator read
   * them as "this act has no gigs".
   */
  event_counts_known?: boolean;
  /** `merge_state_broken`. */
  merge_shape?: "self" | "dangling" | "chain" | null;
  merge_target_id?: string | null;
  /** `merged_artist_on_live_lineup`: the live event still billing a hidden artist. */
  event_id?: string | null;
  event_name?: string | null;
  /**
   * `merged_artist_on_live_lineup` only: the EXACT number of live events billing this merged
   * artist. The audit caps the per-event cards at 50, so when this exceeds the cards shown the
   * curator is looking at a sample.
   */
  live_event_count?: number;
  /**
   * `deceased_artist_with_future_events` only: the Wikidata person whose Spotify id (P1902)
   * matches. Shown so a curator can verify — Wikidata is open-edit and can be wrong.
   */
  wikidata_qid?: string | null;
  wikidata_url?: string | null;
  /** `deceased_artist_with_future_events` only: Wikidata P570, `YYYY-MM-DD`. */
  died_on?: string | null;
  /** `deceased_artist_with_future_events` only: EXACT count of future live events. */
  future_event_count?: number;
  /** `deceased_artist_with_future_events` only: the soonest few (at most 5), soonest first. */
  future_events?: ArtistAuditEvidenceEvent[];
}

/** One row of `GET /artist-audit/findings` — tina's `ArtistAuditFindingRead`. */
export interface ArtistAuditFindingRow {
  id: string;
  /** Typed as the union, read as wire data: fall back rather than assume exhaustiveness. */
  kind: ArtistAuditKind;
  severity: ArtistAuditSeverity;
  /** `kind:key` — stable identity, and all an unknown kind can be shown by. */
  fingerprint: string;
  evidence: ArtistAuditEvidence;
  first_seen_at: string;
  last_seen_at: string;
  dismissed_at: string | null;
  /** The curator's Firebase uid, as IMS sent it. */
  dismissed_by: string | null;
  dismissed_note: string | null;
  /**
   * Set when a curator APPROVED a "possibly deceased" card. An approved row also carries
   * `dismissed_at` (an approval is a dismissal with a verdict), so read this one first.
   * Optional: an older orchestrator does not send it.
   */
  approved_at?: string | null;
  approved_by?: string | null;
  /** Set by the audit once the finding stopped firing — e.g. after a curator merged. */
  resolved_at: string | null;
}

/** Body of `POST /artist-audit/findings/{id}/approve` (deceased cards only). */
export interface ArtistAuditApprovePayload {
  /** Must be a Firebase uid, for the same reason as `dismissed_by`. */
  approved_by: string;
}

/** Body of `POST /artist-audit/findings/{id}/dismiss`. */
export interface ArtistAuditDismissPayload {
  /** Must be a Firebase uid: the API authenticates the service, not the person. */
  dismissed_by: string;
  note?: string | null;
}

/** What `POST /artist-audit/run` reports — an EXECUTION, not an outcome. See `VenueAuditRunResponse`. */
export interface ArtistAuditRunResponse {
  kind: "noop" | "dispatched" | "error";
  execution_name?: string | null;
  error_message?: string | null;
}

// ---- Area audit ----------------------------------------------------------------
//
// tina's `GET /area-audit/findings`: what the venue audit job's nightly area step files
// about Mood's areas and the venues placed in them. It replaced the Issues tab of `/areas`
// on 2026-11-01. Same discipline as the two audit blocks above: the kind list is a const
// tuple, so the label/hint maps in `@/lib/tina/area-audit` are
// `Record<AreaAuditKind, string>` and a ninth check cannot arrive unlabelled. The route
// mirrors `/venue-audit/findings` (params, envelope, order, dismiss body); there is no
// `/area-audit/run`, because the area step runs inside the venue audit job.

/**
 * The checks the nightly area step files: eight issues plus two info kinds that record an
 * unattended prod write (a part set, a border dropped). Left-out venues are deliberately NOT one.
 */
export const AREA_AUDIT_KINDS = [
  "area_mislabelled_venue",
  "area_unplaced_venue",
  "area_venue_on_part",
  "area_without_part",
  "area_small_part",
  "area_city_held_back",
  "area_empty",
  "area_auto_review",
  "area_part_assigned",
  "area_border_dropped",
] as const;
export type AreaAuditKind = (typeof AREA_AUDIT_KINDS)[number];

/** Three levels, unlike the two audits above: `medium` is a venue or area left unplaced. */
export const AREA_AUDIT_SEVERITIES = ["high", "medium", "low"] as const;
export type AreaAuditSeverity = (typeof AREA_AUDIT_SEVERITIES)[number];

/** The four views `GET /area-audit/findings` serves; `open` is its default. */
export const AREA_AUDIT_STATUSES = [
  "open",
  "dismissed",
  "resolved",
  "all",
] as const;
export type AreaAuditStatus = (typeof AREA_AUDIT_STATUSES)[number];

/** A row's own lifecycle state — every status but the `all` view. */
export type AreaAuditFindingStatus = Exclude<AreaAuditStatus, "all">;

/**
 * The `evidence` jsonb, passed through verbatim by the route. Every field is optional:
 * each kind fills its own subset, and a new field must not need a coordinated IMS deploy.
 * The kind summary in `@/lib/tina/area-audit` reads the fields below and, when a kind's
 * own field is missing, lists whatever scalar fields did arrive, so a renamed key shows up
 * as raw text rather than as a blank cell.
 */
export interface AreaAuditEvidence {
  /** `area_mislabelled_venue`: the area the venue is labelled with. */
  label_area?: string | null;
  /** `area_mislabelled_venue`: the other area whose shape holds the venue's point. */
  holding_area?: string | null;
  /** `area_mislabelled_venue`: metres from the labelled area's centre. */
  distance_m?: number | null;
  /** `area_mislabelled_venue`: the venue's street address. */
  address?: string | null;
  /** `area_unplaced_venue`: the venue's own area label, which matched no area. */
  label?: string | null;
  /**
   * `area_venue_on_part`: the part the venue's `areaId` points at. `area_part_assigned` and
   * `area_border_dropped`: the part involved.
   */
  part_name?: string | null;
  /** `area_without_part`: how many parts the city has. */
  city_parts?: number | null;
  /** `area_without_part`: true when the area's part was deleted. */
  part_gone?: boolean | null;
  /** `area_small_part`: how many areas the part holds. */
  children?: number | null;
  /** `area_city_held_back`: why the area step held the city back. */
  reason?: string | null;
  /** `area_auto_review`: when the area step created the area, ISO. */
  created_at?: string | null;
  [key: string]: unknown;
}

/** One row of `GET /area-audit/findings` — tina's area audit finding read model. */
export interface AreaAuditFindingRow {
  id: string;
  /** Typed as the union, read as wire data: fall back rather than assume exhaustiveness. */
  kind: AreaAuditKind;
  severity: AreaAuditSeverity;
  status: AreaAuditFindingStatus;
  city: string;
  country_code: string;
  /** Null for a city-level finding (`area_city_held_back`). */
  area_id: string | null;
  area_name: string | null;
  /** Set only on the three venue-keyed kinds. */
  venue_id: string | null;
  venue_name: string | null;
  evidence: AreaAuditEvidence;
  first_seen_at: string;
  last_seen_at: string;
  /** Set by the audit once the finding stopped firing. */
  resolved_at: string | null;
  dismissed_at: string | null;
  /** Tina's dedup key for the finding. */
  fingerprint?: string;
  /** Who dismissed the finding (a Firebase uid), as the venue audit names it. */
  dismissed_by?: string | null;
  /** The curator's note, as the dismiss body's `note` stored it. */
  dismissed_note?: string | null;
  /** An older name for `dismissed_note`; read only as a fallback. */
  dismissed_reason?: string | null;
}

/** Body of `POST /area-audit/findings/{id}/dismiss` — the venue audit's dismiss body. */
export interface AreaAuditDismissPayload {
  /** Must be a Firebase uid: the API authenticates the service, not the person. */
  dismissed_by: string;
  note?: string | null;
}

// ---- Site health --------------------------------------------------------------
//
// tina's `GET /site-health/findings`. Same discipline as the venue-audit and
// artist-audit blocks above: the check list is a const tuple so `CHECK_LABEL` is
// a `Record<SiteHealthCheck, string>` and a ninth check cannot arrive unlabelled.
// Spec: tina docs/superpowers/specs/2026-09-23-site-health-design.md.

/** The eleven site-health checks — tina's `SiteHealthCheck` Literal, verbatim. */
export const SITE_HEALTH_CHECKS = [
  "discover_failing",
  "discover_empty",
  "pages_unreadable",
  "new_links_fade",
  "social_quiet",
  "mostly_junk",
  "link_count_drop",
  "selector_empty",
  "images_lost",
  "cluttered",
  "tiers_missing",
] as const;
export type SiteHealthCheck = (typeof SITE_HEALTH_CHECKS)[number];
export type SiteHealthSeverity = "red" | "amber";
export type SiteHealthStatus = "open" | "snoozed" | "resolved";

export interface SiteHealthSideReads {
  unreadable: number;
  tried: number;
  share: number;
  crossed: boolean;
  top_reasons: string[];
  sample_event_ids: string[];
}

export interface SiteHealthWeekMix {
  n: number;
  useful_share: number;
  junk_share: number;
}

/**
 * The evidence jsonb, read as wire data: every field is optional because each check writes its
 * own subset (tina spec §5.3), and a newer tina may add fields this build does not know.
 */
export interface SiteHealthEvidence {
  kind?: string;
  streak?: number;
  since?: string;
  last_error_type?: string | null;
  last_error_message?: string | null;
  last_found_at?: string | null;
  last_found_count?: number | null;
  last_run_id?: string;
  last_seed_event_id?: string;
  /** `discover_failing` on an Instagram/Facebook bucket seed: the whole batch failed (tina ruling R22). */
  batched?: boolean;
  scrape?: SiteHealthSideReads | null;
  refresh?: SiteHealthSideReads | null;
  recent_new?: number;
  normal_new?: number;
  ratio?: number;
  days_quiet?: number;
  last_event_at?: string | null;
  profile_unavailable?: boolean;
  /** `social_quiet`: Instagram confirmed the profile is gone — red at once (tina ruling R21). */
  profile_confirmed_gone?: boolean;
  this_week?: SiteHealthWeekMix;
  last_week?: SiteHealthWeekMix;
  top_reasons?: string[];
  sample_event_ids?: string[];
  recent_counts?: number[];
  prior_median?: number;
  n?: number;
  no_rows?: number;
  share?: number;
  /** `images_lost`: pages whose declared poster (og/JSON-LD/twitter/discover) was read in 3 days… */
  tried?: number;
  /** …and how many of them lost it — no declared poster survived download. */
  lost?: number;
  /** `cluttered`: pages read in 14 days (latest read per URL). */
  pages?: number;
  /** `cluttered`: median share of a page that is template text repeated on most pages (0 under 1000 chars). */
  boilerplate_share?: number;
  /** `cluttered`: median template chars per page. */
  boilerplate_chars?: number;
  /** `cluttered`: share of pages linking to 3+ other events of the same site. */
  sibling_page_share?: number;
  /** `cluttered`: up to 5 repeated lines (first 60 chars), most frequent first. */
  template_lines?: string[];
  /** `cluttered`: the heading above the other-event links, e.g. "You may also like". */
  block_heading?: string | null;
  median_raw_chars?: number;
  median_clean_chars?: number;
  /** `cluttered`: up to 3 page URLs that show the clutter best. */
  sample_urls?: string[];
  /** `tiers_missing`: distinct URLs read in 3 days whose latest lookup carried an adapter and found no tiers. */
  events?: number;
  /** `tiers_missing`: the adapter names those reads resolved to (usually one). */
  adapters?: string[];
}

/** One row of `GET /site-health/findings` — tina's `SiteHealthFindingRead`. */
export interface SiteHealthFindingRow {
  id: string;
  site: string;
  check_name: SiteHealthCheck;
  severity: SiteHealthSeverity;
  evidence: SiteHealthEvidence;
  first_seen_at: string;
  last_seen_at: string;
  resolved_at: string | null;
  /** Free-form on the wire; tina sets `"solved"` for a curator's Solved click. */
  resolved_reason: string | null;
  /** The curator's Firebase uid on a Solved close, `null` otherwise. Added alongside `POST /site-health/findings/{id}/solve`. */
  resolved_by?: string | null;
  snoozed_until: string | null;
  snoozed_by: string | null;
  snoozed_note: string | null;
  snoozed_severity: SiteHealthSeverity | null;
  snoozed: boolean;
  platform: string | null;
  city: string | null;
  country_code: string | null;
  x_strategy: string | null;
  content_type: string | null;
}

export interface SiteHealthFindingsPage {
  items: SiteHealthFindingRow[];
  total: number;
  limit: number;
  offset: number;
  state: {
    last_run_at: string | null;
    last_run_status: "ok" | "partial" | "failed" | null;
  };
}

/** What `POST /site-health/run` reports — an EXECUTION, not an outcome. See `VenueAuditRunResponse`. */
export interface SiteHealthRunResponse {
  kind: "noop" | "dispatched" | "error";
  execution_name?: string | null;
  error_message?: string | null;
}

export interface SiteHealthDisableResponse {
  site: string;
  enabled: boolean;
  closed_findings: number;
}

export type TagSuggestionStatus = "open" | "snoozed" | "accepted";

export interface TagSuggestionStats {
  suggestion_id: number;
  hits_window: number;
  max_city_window: number;
  hits_total: number;
  top_cities: { city: string; count: number }[];
}

export interface TagSuggestionExample {
  event_id: string;
  name: string | null;
  venue: string | null;
  start: string | null;
  url: string | null;
  admin_event_id: string | null;
}

export interface TagSuggestionRead {
  id: number;
  name: string;
  description: string;
  status: TagSuggestionStatus;
  snoozed_until: string | null;
  notified_at: string | null;
  first_seen_at: string;
  last_seen_at: string;
  stats: TagSuggestionStats;
  ready: boolean;
  examples: TagSuggestionExample[];
}

/** One row of `GET /cost-subscriptions` — a flat monthly plan (Browserbase, RapidAPI…). */
export interface CostSubscriptionRead {
  id: string;
  name: string;
  vendor: string;
  /** Decimal serialized as a string. */
  monthly_usd: string;
  /** `YYYY-MM-DD`. */
  starts_on: string;
  /** `YYYY-MM-DD`; null while the plan is active. */
  ends_on: string | null;
  note: string | null;
  created_at: string;
  updated_at: string;
}
